import { useMemo, useEffect, useRef, useCallback } from 'react';
import { useStateIfMounted } from 'use-state-if-mounted';
import {
  Box,
  Drawer,
  IconButton,
  InputAdornment,
  styled,
  TextField,
  useTheme,
} from '@mui/material';
import { Close as CloseIcon, Search as SearchIcon } from '@mui/icons-material';
import debounce from 'lodash.debounce';

import searchService, { searchCache } from 'services/search';

import { SearchResultContainer } from '.';

const SearchFieldContainer = styled(Box)(({ theme }) => ({
  flex: 1,
  position: 'fixed',
  padding: '0 !important',
  width: '100%',
  borderBottom: `1px solid ${theme.palette.border.separator}`,
}));

const SearchTextField = styled(TextField)(({ theme }) => ({
  background: theme.palette.background.secondary,
  flex: 1,
  letterSpacing: '0.25px',
  width: '100%',
  height: 50,
  '& fieldset': {
    border: 'none',
    borderRadius: 8,
  },
  '& input::placeholder': {
    fontSize: 16,
    fontWeight: 400,
    color: theme.palette.text.secondary,
    opacity: 1,

    '@media (max-width: 1023px)': {
      fontSize: 13,
    },
  },
  '& input': {
    color: theme.palette.text.main,
    fontSize: 16,
    fontWeight: 400,
    padding: 0,
    width: '100%',
    height: 50,

    '@media (max-width: 1023px)': {
      fontSize: 13,
    },
    '@media (max-width: 904px)': {
      fontSize: 16,
    },
  },
  '&:has(input:focus-within) + div': {
    display: 'block',
  },

  '&:hover': {
    background: theme.palette.background.apply.hovered,
    '& .MuiOutlinedInput-notchedOutline': {
      borderColor: theme.palette.border.decorative,
    },
  },
  '&:active': {
    background: theme.palette.background.apply.pressed,
    '& .MuiOutlinedInput-notchedOutline': {
      borderColor: theme.palette.border.decorative,
    },
  },
  '& .MuiInputBase-root.Mui-focused': {
    background: theme.palette.background.default,
  },
  '& .MuiOutlinedInput-root:focus-within': {
    background: theme.palette.background.default,
  },
}));

const SearchDrawer = ({ open, onClose }) => {
  const theme = useTheme();
  const searchContainerRef = useRef(null);
  const inputRef = useRef(null);

  const [isLoading, setIsLoading] = useStateIfMounted(false);
  const [error, setError] = useStateIfMounted(false);
  const [searchText, setSearchText] = useStateIfMounted('');
  const [searchResult, setSearchResult] = useStateIfMounted({});
  const [isInputFocused, setIsInputFocused] = useStateIfMounted(false);

  // Search optimization refs
  const searchAbortControllerRef = useRef(null);

  useEffect(() => {
    if (open) {
      setTimeout(() => {
        const input = inputRef.current?.querySelector('input');
        if (input) {
          input.click();
          input.focus();
        }
      }, 150);
    }
  }, [open]);

  const handleClickOutside = useCallback((event) => {
    if (searchContainerRef.current && !searchContainerRef.current.contains(event.target)) {
      setIsInputFocused(false);
      setSearchText('');
      setSearchResult({});
    }
  }, []);

  useEffect(() => {
    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [handleClickOutside]);

  const latestQueryRef = useRef('');

  const handleSearch = useCallback(async (input) => {
    // Cancel any in-flight request
    if (searchAbortControllerRef.current) {
      searchAbortControllerRef.current.abort();
    }

    // Create new abort controller
    const abortController = new AbortController();
    searchAbortControllerRef.current = abortController;

    latestQueryRef.current = input;

    setIsLoading(true);
    setError(false);

    try {
      const response = await searchService.search(input, abortController.signal);

      if (!abortController.signal.aborted && latestQueryRef.current === input) {
        // Cache the result
        const cacheKey = input.toLowerCase().trim();
        searchCache.set(cacheKey, response);

        setSearchResult(response);
        setIsLoading(false);
      }
    } catch (err) {
      if (err.name !== 'AbortError') {
        console.error('Search error:', err);
        setError(true);
        setIsLoading(false);
      }
    } finally {
      if (searchAbortControllerRef.current === abortController) {
        searchAbortControllerRef.current = null;
      }
    }
  }, []);

  const debouncedApiCall = useMemo(
    () => debounce(handleSearch, 450), // Only API calls are debounced
    [handleSearch],
  );

  // Immediate cache check + debounced API call
  const handleSearchInput = useCallback(
    (input) => {
      setSearchText(input);

      // Clear results if empty search
      if (!input || input.trim().length === 0) {
        setSearchResult({});
        setIsLoading(false);
        return;
      }

      // Check cache immediately for instant results
      const cacheKey = input.toLowerCase().trim();
      let cachedResult = searchCache.getExactFresh(cacheKey);

      // If no exact match, check for prefix matches
      if (!cachedResult && cacheKey.length >= 2) {
        cachedResult = searchCache.getPrefixFilteredFresh(cacheKey);
      }

      if (cachedResult) {
        // Show cached results immediately
        setSearchResult(cachedResult);
        setIsLoading(false);
      } else if (input.trim().length >= 3) {
        // No cache hit, trigger debounced API call
        setIsLoading(true);
        debouncedApiCall(input);
      } else {
        // For < 3 chars with no cache, avoid network and stop loading
        setIsLoading(false);
      }
    },
    [debouncedApiCall],
  );

  const showSearchResultContainer = useMemo(
    () => searchText && searchText.length > 2,
    [searchText],
  );

  return (
    <Drawer
      disableRestoreFocus
      transitionDuration={0}
      anchor="top"
      open={open}
      onClose={onClose}
      sx={{ zIndex: 1320 }}
    >
      <SearchFieldContainer ref={searchContainerRef}>
        <SearchTextField
          autoFocus
          ref={inputRef}
          placeholder="Search"
          icon={SearchIcon}
          InputProps={{
            startAdornment: (
              <InputAdornment position="start" style={{ color: theme.palette.icon.tertiary }}>
                <SearchIcon />
              </InputAdornment>
            ),
            endAdornment: (
              <InputAdornment position="end">
                <IconButton onClick={onClose}>
                  <CloseIcon style={{ color: theme.palette.icon.tertiary }} />
                </IconButton>
              </InputAdornment>
            ),
          }}
          onChange={(e) => {
            handleSearchInput(e.target.value);
          }}
          onFocus={() => setIsInputFocused(true)}
        />
        {(showSearchResultContainer || isInputFocused) && (
          <SearchResultContainer
            isLoading={isLoading}
            searchResult={searchResult}
            setSearchText={setSearchText}
            onClose={() => {
              setSearchText('');
              setSearchResult({});
              setIsInputFocused(false);
              onClose();
            }}
            isError={error}
            isInputFocused={isInputFocused}
          />
        )}
      </SearchFieldContainer>
    </Drawer>
  );
};

export default SearchDrawer;
